import React from 'react'
import NotFound from '../Pages/NotFound'
import RuntimeRecoveryFallback from './RuntimeRecoveryFallback'

import { Box, Stack, Typography } from '@mui/material'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSync } from '@fortawesome/pro-duotone-svg-icons/faSync'

import { Trans } from 'react-i18next'
import { StyledButton } from '@/shared/ui/buttons'

export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props)

        this.state = {
            error: null,
            errorInfo: null,
            hasError: false,
        }
    }

    static getDerivedStateFromError(error) {
        return { error, hasError: true }
    }

    componentDidCatch(error, errorInfo) {
        this.setState({
            error: error,
            errorInfo: errorInfo,
        })
    }

    async clearCachesAndRefresh() {
        if (caches) {
            const names = await caches.keys()
            await Promise.all(names.map((name) => caches.delete(name)))
        }

        window.location.reload(true)
    }

    // Show recovery only for authenticated users with missing env (memory + LS).
    // hasEnviroment() restores from localStorage when possible; without isLoggedIn(),
    // idle/logout states could show recovery instead of the login modal/page flow.
    // TODO: Investigate recovery for malformed non-null environment objects (for example, missing id).
    shouldShowEnvironmentRecovery() {
        const kedo = this.props.kedo

        return !!(
            kedo?.env &&
            !kedo.env().hasEnviroment() &&
            kedo?.user?.().isLoggedIn()
        )
    }

    renderEnvironmentRecovery() {
        return (
            <RuntimeRecoveryFallback
                kedo={this.props.kedo}
                runtimeRecoveryType="auth-session-recovery"
            />
        )
    }

    render() {
        const { hasError, error, errorInfo } = this.state

        // Before children mount (e.g. Files) so missing env does not throw under the recovery UI.
        if (!hasError && this.shouldShowEnvironmentRecovery()) {
            return this.renderEnvironmentRecovery()
        }

        if (
            hasError &&
            error &&
            error.toString().toLowerCase().indexOf('chunk') !== -1
        ) {
            return (
                <Box
                    className="mainContainer"
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        p: 3,
                    }}
                >
                    <Stack spacing={2} alignItems="flex-start">
                        <Typography variant="h4">
                            <Trans i18nKey="New release">New release</Trans>
                        </Typography>

                        <Typography>
                            <Trans i18nKey="new_release_description">
                                There is a new release please click on the
                                button to refresh or hit (Ctrl + F5) or (Ctrl +
                                Shift + R).
                            </Trans>
                        </Typography>

                        <Stack direction="row" spacing={2}>
                            <StyledButton
                                size="small"
                                dense
                                variant="contained"
                                onClick={this.clearCachesAndRefresh}
                            >
                                <FontAwesomeIcon icon={faSync} />
                            </StyledButton>

                            <StyledButton
                                size="small"
                                dense
                                variant="contained"
                                onClick={() => window.location.reload(true)}
                            >
                                <FontAwesomeIcon icon={faSync} />
                            </StyledButton>
                        </Stack>
                    </Stack>
                </Box>
            )
        }

        if (hasError && this.shouldShowEnvironmentRecovery()) {
            return this.renderEnvironmentRecovery()
        }

        return hasError ? (
            <NotFound
                {...this.props}
                errorType={'dossier'}
                error={error}
                errorInfo={errorInfo}
            />
        ) : (
            this.props.children
        )
    }
}
